import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { palette } from "styled-theme";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import CreateIcon from "@mui/icons-material/Create";
import TextSelectIcon from "@mui/icons-material/TextFormat";
import DeleteIcon from "@mui/icons-material/Delete";
import BoxIcon from "@mui/icons-material/CropDin";
import CircleIcon from "@mui/icons-material/Lens";
import CircleIconEmpty from "@mui/icons-material/PanoramaFishEye";
import ArrowIcon from "@mui/icons-material/ArrowDownward";
import LineIcon from "@mui/icons-material/Remove";
import VisibilityOnIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MarkerIcon from "~/components/Icon/MarkerIcon";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import PictureInPictureIcon from "@mui/icons-material/PictureInPicture";
import ChangeHistoryTwoToneIcon from "@mui/icons-material/ChangeHistoryTwoTone";
import IconGallery from "~/components/IconGallery";

import {
  FREE_DRAWING,
  FILLED_RECTANGLE,
  DASHED_RECTANGLE,
  FILLED_CIRCLE,
  DASHED_CIRCLE,
  ARROW,
  TEXT,
  MARKER,
  TRIANGLE,
  FILLED_TRIANGLE,
  LINE
} from "~/constants/shape-types";

import CursorDefaultIcon from "~/components/Icon/CursorDefaultIcon";
import RectangleIcon from "~/components/Icon/RectangleIcon";
import ColorPickerButton from "~/components/Button/ColorPickerButton";

import InlineToolbar from "~/components/InlineToolbar";
import { useSelector } from "react-redux";
import { selectShowCutout, selectShowModalCutout } from "~/store/ducks/user/uistate";
import DeleteAllAnnotationsModal from "./DeleteAllAnnotationsModal";

const StyledIconButton = styled(IconButton)(({ theme, ...props }) => {
  return {
    /* background-color: ${props => (props.selected ? palette("grayscale", 2) : "transparent")} !important; */
    /* border: 10px solid rebeccapurple; */
    backgroundColor: `${props.selected ? "rgba(0, 0, 0, 0.12)" : "transparent"} !important`,
    borderRadius: "0 !important",
    "&:focus-visible": {
      outline: theme.outline(),
      outlineOffset: "-2px"
    }
  };
});

const RotatedArrow = styled(ArrowIcon)`
  transform: rotate(-45deg);
`;

function AnnotationeerControls({
  className,
  isAnnotationsVisible,
  toggleAnnotations,
  isDrawingMode,
  toggleDrawingMode,
  setShapeType,
  shapeType,
  deleteSelected,
  setDrawingMode,
  showCursorDefaultButton,
  showAnnotationsShowHideButton,
  showEditModeButton,
  showColorPickerButton,
  showDeleteButton,
  showDeleteAllButton,
  showModal,
  showCutoutButton,
  toggleCutoutShow,
  toggleModalCutoutShow,
  isDialogControls,
  materials,
  // if set, this will delete all annotations
  // instead of open viewers
  optionalToDeleteAnnotationMeta
}) {
  function onSelect(value) {
    setShapeType(value);
    setDrawingMode(true);
  }
  // this not via store because 1) does not need to be saved
  // 2) cannot be Portal/Modal since can be above Viewer in Modal...
  // 3) overhead, needs to be nearby its control and does not need to be more central in app
  // but you can of course move it :-)
  const [showDeleteAllAnnotationsModal, setShowDeleteAllAnnotationsModal] = useState(false);
  const closeDeleteAllAnnotationsModal = useCallback(() => {
    setShowDeleteAllAnnotationsModal(false);
  }, []);

  const shouldShowCutout = useSelector(selectShowCutout);
  const shouldShowModalCutout = useSelector(selectShowModalCutout);
  const showCutout = isDialogControls ? shouldShowModalCutout : shouldShowCutout;
  const onToggleCutout = isDialogControls ? toggleModalCutoutShow : toggleCutoutShow;
  return (
    <InlineToolbar className={className}>
      {showCutoutButton && (
        <Tooltip title="Teilausschnitt">
          <StyledIconButton selected={showCutout} onClick={onToggleCutout} size="large" aria-label="Teilausschnitt">
            <PictureInPictureIcon />
          </StyledIconButton>
        </Tooltip>
      )}
      {showCursorDefaultButton && (
        <Tooltip title="Auswahl">
          <StyledIconButton selected={!isDrawingMode} onClick={toggleDrawingMode} size="large" aria-label="Auswahl">
            <CursorDefaultIcon />
          </StyledIconButton>
        </Tooltip>
      )}
      {showAnnotationsShowHideButton && (
        <Tooltip title={`Anmerkungen ${isAnnotationsVisible ? "ausblenden" : "anzeigen"}`}>
          <StyledIconButton
            onClick={toggleAnnotations}
            size="large"
            aria-label={`Anmerkungen ${isAnnotationsVisible ? "ausblenden" : "anzeigen"}`}
          >
            {isAnnotationsVisible ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
          </StyledIconButton>
        </Tooltip>
      )}
      {showEditModeButton && (
        <IconGallery
          label="Eigene Anmerkungen"
          active={isDrawingMode}
          value={shapeType}
          onChange={onSelect}
          defaultIcon={<CreateIcon value={FREE_DRAWING} />}
        >
          <CreateIcon value={FREE_DRAWING} label="Zeichnen" />
          <MarkerIcon value={MARKER} label="Markieren" />
          <TextSelectIcon value={TEXT} label="Text" />
          <RectangleIcon value={FILLED_RECTANGLE} label="Rechteck ausgefüllt" />
          <BoxIcon value={DASHED_RECTANGLE} label="Rechteck Rahmen" />
          <CircleIcon value={FILLED_CIRCLE} label="Kreis ausgefüllt" />
          <CircleIconEmpty value={DASHED_CIRCLE} label="Kreis Rahmen" />
          <LineIcon value={LINE} label="Linie" />
          <RotatedArrow value={ARROW} label="Pfeil" />
          <ChangeHistoryIcon value={TRIANGLE} label="Dreieck" />
          <ChangeHistoryTwoToneIcon value={FILLED_TRIANGLE} label="Dreieck ausgefüllt" />
        </IconGallery>
      )}
      {showColorPickerButton && <ColorPickerButton />}
      {showDeleteButton && (
        <Tooltip title="Anmerkung löschen">
          <StyledIconButton onClick={deleteSelected} size="large" aria-label="Anmerkung löschen">
            <DeleteIcon />
          </StyledIconButton>
        </Tooltip>
      )}
      {showDeleteAllButton && (
        <>
          <Tooltip title="Alle Anmerkungen löschen">
            <StyledIconButton
              onClick={() => setShowDeleteAllAnnotationsModal(former => !former)}
              size="large"
              aria-label="Alle Anmerkungen löschen"
            >
              <DeleteForeverIcon />
            </StyledIconButton>
          </Tooltip>
          <DeleteAllAnnotationsModal
            show={showDeleteAllAnnotationsModal}
            onClose={closeDeleteAllAnnotationsModal}
            optionalToDeleteAnnotationMeta={optionalToDeleteAnnotationMeta}
          />
        </>
      )}
    </InlineToolbar>
  );
}

AnnotationeerControls.propTypes = {
  className: PropTypes.string,
  isAnnotationsVisible: PropTypes.bool.isRequired,
  toggleAnnotations: PropTypes.func.isRequired,
  isDrawingMode: PropTypes.bool.isRequired,
  toggleDrawingMode: PropTypes.func.isRequired,
  setShapeType: PropTypes.func.isRequired,
  shapeType: PropTypes.string,
  deleteSelected: PropTypes.func.isRequired,
  setDrawingMode: PropTypes.func.isRequired,
  showCursorDefaultButton: PropTypes.bool.isRequired,
  showAnnotationsShowHideButton: PropTypes.bool.isRequired,
  showEditModeButton: PropTypes.bool.isRequired,
  showColorPickerButton: PropTypes.bool.isRequired,
  showDeleteButton: PropTypes.bool.isRequired,
  showModal: PropTypes.func.isRequired,
  showDeleteAllButton: PropTypes.bool.isRequired,
  showCutoutButton: PropTypes.bool.isRequired,
  toggleCutoutShow: PropTypes.func.isRequired
};

export default AnnotationeerControls;
